/* Shared constants/formatting/response-mapping for the AOP Budget report
   (Summary + Details tabs). See APIIntegrationFiles/AOP_BUDGET_API.md for the
   full contract this maps — both endpoints are already camelCase and
   pre-computed server-side (variance, subtotals, grand total), so these
   mappers mostly just pass fields through with safe defaults; the one real
   job they do is normalizing "no data" to an empty array/null rather than
   leaving `undefined` for callers to guard against individually. */

export const METRICS = [
  { key: "revenue", label: "Revenue" },
  { key: "directLabor", label: "Direct Labor" },
  { key: "subcontracts", label: "Subcontracts" },
  { key: "odc", label: "ODC" },
  { key: "grossProfit", label: "Gross Profit" },
];

export function fmtMoney(v) {
  if (v == null || v === "") return "—";
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  const abs = Math.abs(n).toLocaleString("en-US", { maximumFractionDigits: 0 });
  return n < 0 ? `($${abs})` : `$${abs}`;
}

export function fmtDate(v) {
  if (!v) return "—";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v);
  return d.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
}

/** Business-type rows always end with a `"Total"` row — split it off so
 *  table rendering/sorting can keep it pinned last regardless of the sort
 *  applied to the other rows (the API's own fixed-then-alphabetical-then-
 *  Total ordering already puts it last on load; this survives a re-sort). */
export function splitTotalRow(rows = []) {
  const totalIdx = rows.findIndex((r) => r.businessType === "Total");
  if (totalIdx === -1) return { rows, total: null };
  const total = rows[totalIdx];
  const rest = rows.slice(0, totalIdx).concat(rows.slice(totalIdx + 1));
  return { rows: rest, total };
}

/** -> { nextYearVersion, currentYearVersion, currentYear, nextYear,
 *  actualThroughMonth, actualMonths, table1, table2, variance } — table1 =
 *  `nextYearSelectedVersion`, table2 = `currentYearOpenVersion`, both
 *  already server-computed/ordered; `variance` is server-computed too
 *  (never derived client-side — the API guarantees it's internally
 *  consistent with table1/table2 for the same response). */
export function mapAOPBudgetSummary(res) {
  const d = res?.data ?? res ?? {};
  return {
    nextYearVersion: d.nextYearVersion ?? null,
    currentYearVersion: d.currentYearVersion ?? null,
    currentYear: d.currentYear ?? null,
    nextYear: d.nextYear ?? null,
    actualThroughMonth: d.actualThroughMonth ?? null,
    actualMonths: Array.isArray(d.actualMonths) ? d.actualMonths : [],
    table1: Array.isArray(d.nextYearSelectedVersion) ? d.nextYearSelectedVersion : [],
    table2: Array.isArray(d.currentYearOpenVersion) ? d.currentYearOpenVersion : [],
    variance: Array.isArray(d.variance) ? d.variance : [],
  };
}

/** -> { version, nextYear, sections: [{businessType, rows, subtotal}], grandTotal }. */
export function mapAOPBudgetDetails(res) {
  const d = res?.data ?? res ?? {};
  return {
    version: d.version ?? null,
    nextYear: d.nextYear ?? null,
    sections: Array.isArray(d.sections)
      ? d.sections.map((s) => ({
          businessType: s.businessType,
          rows: Array.isArray(s.rows) ? s.rows : [],
          subtotal: s.subtotal ?? null,
        }))
      : [],
    grandTotal: d.grandTotal ?? null,
  };
}
